import React, { useEffect, useState } from 'react';
import Icon from '../../components/AppIcon';

const OAuthCallback = () => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    // Prevent multiple executions
    if (status !== 'processing') return;
    
    // Add a small delay to ensure the popup is fully loaded
    const timer = setTimeout(() => {
      handleCallback();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [status]);

  const handleCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams?.get('code');
      const state = urlParams?.get('state');
      const error = urlParams?.get('error');
      const errorDescription = urlParams?.get('error_description');
      
      // Extract provider from state parameter (format: "provider:randomstate")
      let provider = null;
      if (state && state?.includes(':')) {
        provider = state?.split(':')?.[0];
      }

      // Handle OAuth errors
      if (error) {
        setStatus('error');
        setMessage(errorDescription || 'Authentication failed');
        
        // Send error to parent window
        const errorMsg = {
          type: 'oauth_error',
          provider: provider,
          error: error,
          errorDescription: errorDescription
        };
        if (window.opener) {
          window.opener.postMessage(errorMsg, window.location.origin);
        }
        try {
          const bc = new BroadcastChannel('oauth_channel');
          bc.postMessage(errorMsg);
          bc.close();
        } catch (_) {}
        
        setTimeout(() => window.close(), 3000);
        return;
      }

      if (!code || !provider) {
        throw new Error('Missing authorization code or provider');
      }

      // Note: sessionStorage is NOT shared between popup and parent window
      // Parent window will verify authentication when it gets fresh session token

      setStatus('success');
      setMessage('Authentication successful! Closing window...');

      // Send success message to parent window
      const successMsg = {
        type: 'oauth_success',
        provider: provider,
        code: code,
        state: state
      };

      // Try postMessage first (works when window.opener survives cross-origin redirects)
      if (window.opener) {
        window.opener.postMessage(successMsg, window.location.origin);
      }
      // Always also broadcast via BroadcastChannel — reliable fallback when
      // window.opener is nulled after cross-origin OAuth redirects
      try {
        const bc = new BroadcastChannel('oauth_channel');
        bc.postMessage(successMsg);
        bc.close();
      } catch (_) { /* BroadcastChannel not supported — postMessage is only path */ }

      // Clean up user ID from sessionStorage
      sessionStorage.removeItem(`oauth_user_id_${provider}`);

      // Close popup after short delay
      setTimeout(() => {
        window.close();
      }, 1500);
    } catch (error) {
      console.error('OAuth callback error:', error?.message);
      setStatus('error');
      setMessage(error?.message || 'Authentication failed');
      
      // Try to extract provider from URL state if available
      const urlParams = new URLSearchParams(window.location.search);
      const state = urlParams?.get('state');
      let provider = null;
      if (state && state?.includes(':')) {
        provider = state?.split(':')?.[0];
      }
      
      const catchErrorMsg = {
        type: 'oauth_error',
        provider: provider,
        error: error?.message
      };
      if (window.opener) {
        window.opener.postMessage(catchErrorMsg, window.location.origin);
      }
      try {
        const bc = new BroadcastChannel('oauth_channel');
        bc.postMessage(catchErrorMsg);
        bc.close();
      } catch (_) {}
      
      setTimeout(() => window.close(), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="bg-card rounded-xl shadow-lg border border-border p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
              <Icon name="Loader" size={32} className="text-muted-foreground animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Processing Authentication</h2>
            <p className="text-muted-foreground">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 bg-emerald-500/10 rounded-full flex items-center justify-center">
              <Icon name="CheckCircle" size={32} className="text-emerald-500" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Success!</h2>
            <p className="text-muted-foreground">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center">
              <Icon name="AlertCircle" size={32} className="text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Authentication Failed</h2>
            <p className="text-muted-foreground">{message}</p>
            <p className="text-sm text-muted-foreground mt-2">This window will close automatically...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;